// PRD §39 — Standardised states the /app/report surface can fall back
// to. One illustrative primitive per terminal condition so future surfaces
// (digest, share URL, print) can reuse the same visual language and copy.

import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface StateShellProps {
  glyph: string;
  glyphTone: "neutral" | "amber" | "red" | "purple";
  title: ReactNode;
  description: ReactNode;
  action?: ReactNode;
}

function StateShell({ glyph, glyphTone, title, description, action }: StateShellProps) {
  const tone =
    glyphTone === "amber"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : glyphTone === "red"
        ? "bg-red-50 text-red-700 ring-red-100"
        : glyphTone === "purple"
          ? "bg-purple-50 text-purple-700 ring-purple-100"
          : "bg-neutral-100 text-neutral-700 ring-neutral-200";
  return (
    <div className="rounded-2xl bg-white p-8 text-center ring-1 ring-neutral-950/5">
      <div className="mx-auto flex max-w-md flex-col items-center gap-4">
        <span
          className={`inline-flex size-12 shrink-0 items-center justify-center rounded-full ring-1 ${tone}`}
        >
          <i className={`${glyph} text-lg`} aria-hidden="true" />
        </span>
        <h3 className="font-display text-lg font-semibold text-neutral-950">{title}</h3>
        <p className="text-sm text-neutral-600 text-pretty">{description}</p>
        {action ? <div className="pt-2">{action}</div> : null}
      </div>
    </div>
  );
}

export function LoadingReport() {
  return (
    <StateShell
      glyph="fa-regular fa-circle-notch fa-spin"
      glyphTone="purple"
      title="Pulling fresh GA4 numbers"
      description="We're aggregating sessions, engagement, conversions and UTM hygiene for the period. This usually takes a few seconds."
    />
  );
}

export function ReportError({ message }: { message: string }) {
  // 401s from the worker surface as "Access token expired — reconnect
  // GA4" in the message. When that's the cause, lead with the Connect
  // CTA (primary) and keep "Try again" as secondary — a reload alone
  // can't fix an expired refresh token.
  const looksLikeAuthFailure =
    /401|access token expired|reconnect/i.test(message) || /unauthor/i.test(message);

  const returnTo =
    typeof window === "undefined"
      ? "/app/report"
      : window.location.pathname + window.location.search;

  return (
    <StateShell
      glyph="fa-regular fa-circle-xmark"
      glyphTone={looksLikeAuthFailure ? "amber" : "red"}
      title={looksLikeAuthFailure ? "Your GA4 connection expired" : "We couldn't load this report"}
      description={
        looksLikeAuthFailure
          ? "Google rotated the access token we use to read this property. Reconnect GA4 to pick things back up — the report will reload automatically once you're back."
          : message
      }
      action={
        looksLikeAuthFailure ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button asChild>
              <Link to={`/app/connect?return_to=${encodeURIComponent(returnTo)}`}>
                Reconnect GA4
                <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
              </Link>
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <i className="fa-solid fa-rotate-right text-sm" aria-hidden="true" />
              Try again
            </Button>
          </div>
        ) : (
          <Button onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right text-sm" aria-hidden="true" />
            Try again
          </Button>
        )
      }
    />
  );
}

export function EmptySocial() {
  return (
    <StateShell
      glyph="fa-regular fa-share-nodes"
      glyphTone="neutral"
      title="No social sessions in this period"
      description="GA4 didn't surface any Organic or Paid Social sessions for this property. Pick a longer date range, or double-check that your campaign URLs use utm_medium=social, organic, or paid_social."
    />
  );
}

export function NoKeyEvents() {
  return (
    <StateShell
      glyph="fa-regular fa-bullseye-arrow"
      glyphTone="amber"
      title="No key events configured"
      description="Sessions are arriving, but GA4 has no key events to count as conversions. Mark a key event in GA4 Admin → Events to see your conversion funnel here."
    />
  );
}

export function QuotaExhausted() {
  return (
    <StateShell
      glyph="fa-regular fa-gauge-circle-bolt"
      glyphTone="amber"
      title="GA4 quota hit for this property"
      description="Google's Data API rate limit kicked in. The next refresh runs after the per-property quota resets — usually within an hour. Cached numbers stay available until then."
    />
  );
}

export function PropertyUnsupported() {
  return (
    <StateShell
      glyph="fa-regular fa-circle-info"
      glyphTone="amber"
      title="This property type isn't supported yet"
      description="Traffic Snapshot reports against GA4 web properties. Universal Analytics 360 properties and other variants will land in a later release."
      action={
        <Button asChild variant="outline">
          <Link to="/app/properties">Choose a different property</Link>
        </Button>
      }
    />
  );
}
